import { currentAccessToken } from "@/shared/api/client";
import { ApiError, NETWORK_ERROR, apiErrorFrom } from "@/shared/api/errors";

/* Calls that openapi-fetch cannot make: multipart uploads and stored files streamed behind the
   session's token. Billing (receipts, contracts) and HR (employee documents) share them. */

function authorization(): Record<string, string> {
  const token = currentAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface UploadOptions {
  method?: "PUT" | "POST";
  /** More form fields sent beside the file, such as a document's kind and name. */
  fields?: Readonly<Record<string, string>>;
}

/** Send a file as multipart, through fetch with the session's token; resolves to the data. */
export async function uploadFile<T>(
  path: string,
  file: File,
  { method = "PUT", fields = {} }: UploadOptions = {},
): Promise<T> {
  const body = new FormData();
  body.append("file", file);
  for (const [name, value] of Object.entries(fields)) body.append(name, value);
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      body,
      credentials: "same-origin",
      headers: authorization(),
    });
  } catch {
    throw new ApiError(NETWORK_ERROR, "Network request failed", 0);
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) throw apiErrorFrom(payload, response);
  return (payload as { data: T }).data;
}

/**
 * Open a stored file in a new tab: fetched with the token, shown from a local object URL. The
 * tab opens during the click, so no pop-up blocker stops it, and closes again on failure.
 */
export async function openFile(path: string): Promise<void> {
  const opened = window.open("", "_blank");
  let response: Response;
  try {
    response = await fetch(path, { credentials: "same-origin", headers: authorization() });
  } catch {
    opened?.close();
    throw new ApiError(NETWORK_ERROR, "Network request failed", 0);
  }
  if (!response.ok) {
    opened?.close();
    throw apiErrorFrom(await response.json().catch(() => null), response);
  }
  const url = URL.createObjectURL(await response.blob());
  if (opened) opened.location.href = url;
  else window.location.href = url;
}
